import { useStatsigClient } from '@statsig/react-bindings'

export const STATSIG_GATE_NAMES = {
  SUBSCRIPTIONS_ENABLED: 'subscriptions_enabled',
  SUBSCRIPTION_OTP_ENABLED: 'subscription_otp_enabled',
  SUBSCRIPTION_ADDRESS_UPDATE_MESSAGE: 'subscription_address_update_message',
  DECAGON_ENABLED_FOR_LOGGED_OUT_USERS: 'decagon_enabled_for_logged_out_users',
  INSEAM_ATTRIBUTE_ENABLED: 'inseam_attribute_enabled',
  VDA_ORDER_CHANGE_ENABLED: 'vda_order_change_enabled',
  DISABLE_EDD_HOMEPAGE_RECOMMENDATIONS: 'disable_edd_homepage_recommendations',
  MIGRATE_WHITE_GLOVE_DELIVERY_TAG: 'migrate_white_glove_delivery_tag',
  EFA_CHECKOUT_REDIRECT: 'efa_checkout_redirection',
  EFA_PHASE1: 'efaphase1',
  CLP_RESPONSIVE_FILTERS: 'clp_responsive_filters',
  RESPONSIVE_CTA_LOADING_ENABLED: 'ssr_responsive_cta',
  // Kill switch for the PL buy box: ON forces the client /active-inventory call even when the PL
  // payload carries activeInventory. OFF (default) = payload-first with automatic client fallback.
  PL_FORCE_CLIENT_INVENTORY: 'pl_force_client_inventory',
  CLP_SUBCOLLECTION_PATH_REMOUNT: 'clp_subcollection_path_remount',
  // Quick-ATC popup data source. ON = fetch the QATC PL layout (`pageType: QATC`) and render the
  // PRODUCT_GALLERY + PRODUCT_PURCHASE widgets. OFF (default) = today's `useFullProductInterceptor`
  // path (`/refresh` or SAND `/products/enrich` → a domain Product). Any QATC fetch failure or empty
  // widget payload also falls back to that path, so this gate is the deliberate switch, not the
  // safety net.
  //
  // Value MUST match the gate key in Statsig exactly — `checkGate` on an unknown name silently returns
  // false, so a typo reads as "permanently off" with no error anywhere.
  // Console: https://console.statsig.com/1FagQgwoVYHQOUj4R2Hion/gates/web_qatc_enabled
  WEB_QATC_ENABLED: 'web_qatc_enabled',
  // The quick-add v2 surface ("cart popup two"): a right-anchored sidebar on desktop, bottom sheet on
  // mobile, rendering the QATC PL layout vertically (header row → gallery → buy box). ON = mount
  // `QatcSidebar`; OFF (default) = fall through to the `web_qatc_enabled` switch above. Independent of
  // that gate so the two surfaces roll out (and roll back) separately. Runtime fetch failures fall back
  // to the legacy popup regardless of this gate.
  // Console: https://console.statsig.com/1FagQgwoVYHQOUj4R2Hion/gates/web_qatc_sidebar_enabled
  WEB_QATC_SIDEBAR_ENABLED: 'web_qatc_sidebar_enabled',
  WEB_SHOW_FLOWER_PRINTED_CARD: 'web_show_flower_printed_card',
} as const

export const STATSIG_DYNAMIC_CONFIG_NAMES = {
  ENABLE_WRITE_REVIEW_ON_B2B_PDP: 'enableWriteReviewOnB2BPdp',
} as const

export type StatsigGateName = (typeof STATSIG_GATE_NAMES)[keyof typeof STATSIG_GATE_NAMES]
/**
 * Hook to check Statsig gate status
 * @param gateName - The name of the gate to check
 * @returns boolean - true if gate is enabled, false otherwise
 */
export const useStatsigGate = (gateName: StatsigGateName): boolean => {
  const { checkGate } = useStatsigClient()
  try {
    return checkGate(gateName)
  } catch (error) {
    // Log the error but don't throw to prevent breaking non-critical features
    console.error('Failed while checking statsig gate', error)
    return false
  }
}
